// src/components/routes/RouteGrid.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
    Container, Card, CardBody, CardTitle, Button,
    Table, Modal, ModalHeader, ModalBody, Spinner, Row, Col, FormGroup, Input, Label
} from "reactstrap";
import Session from "../auth/Session";
import SurchargeForm from "./SurchargeForm";
import SearchableDropdownFilter from "./SearchableDropdownFilter";
import useSortableData from "./useSortableData";
import SortableHeader from "./SortableHeader";

const getSubCustomerLabel = (subCustomer) =>
    `${subCustomer.name}${subCustomer.customer ? ` — ${subCustomer.customer.name}` : ""}`;

const getSurchargeTargetType = (surcharge) =>
    surcharge.targetType ?? (surcharge.driverId ? "Driver" : (surcharge.routeId ? "Route" : "SubCustomer"));

const isCustomerRouteSurcharge = (surcharge) => getSurchargeTargetType(surcharge) !== "Driver";
const isVisibleSurcharge = (surcharge, includeInactive) =>
    isCustomerRouteSurcharge(surcharge) && (includeInactive || surcharge.isActive !== false);

const SurchargeGrid = () => {
    const apiBaseUrl = "/api/surcharges";
    const subCustomersApi = "/api/subcustomers";

    const [subCustomers, setSubCustomers] = useState([]);
    const [selectedSubCustomerId, setSelectedSubCustomerId] = useState("");
    const [includeInactive, setIncludeInactive] = useState(false);
    const [surcharges, setSurcharges] = useState([]);
    const [loading, setLoading] = useState(false);

    const [editing, setEditing] = useState(null);
    const [open, setOpen] = useState(false);

    const sortGetters = useMemo(() => ({
        subCustomer: (s) => s.subCustomer?.name ?? "",
        route: (s) => s.route?.name ?? "",
        transactionType: (s) => s.transactionType?.transactionName ?? s.transactionTypeId,
        surchargeType: (s) => s.surchargeType?.name ?? "Dollar Amount",
        amount: (s) => Number(s.surchargeType?.name === "Percentage" ? s.percentage : s.dollarAmount),
        status: (s) => (s.isActive === false ? "Inactive" : "Active"),
    }), []);

    const { items: sortedSurcharges, sortConfig, requestSort } = useSortableData(
        surcharges,
        { key: "subCustomer", direction: "asc", getters: sortGetters }
    );

    // Load dropdown options once
    useEffect(() => {
        (async () => {
            try {
                const token = await Session.getToken();
                const res = await fetch(subCustomersApi, { headers: { Authorization: `Bearer ${token}` } });
                const data = await res.json();
                setSubCustomers(data);
            } catch (e) {
                console.error("Failed to load subcustomers", e);
            }
        })();
    }, []);

    // Load all surcharges by default, or filter by selected sub-customer.
    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const token = await Session.getToken();
                const url = selectedSubCustomerId ? `${apiBaseUrl}/BySubCustomer/${selectedSubCustomerId}` : apiBaseUrl;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
                const data = await res.json();
                setSurcharges((data || []).filter(s => isVisibleSurcharge(s, includeInactive)));
            } catch (e) {
                console.error("Failed to load surcharges", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [selectedSubCustomerId, includeInactive]);

    const handleOpenNew = () => {
        setEditing(null);
        setOpen(true);
    };

    const handleSaved = () => {
        setOpen(false);
        (async () => {
            setLoading(true);
            try {
                const token = await Session.getToken();
                const url = selectedSubCustomerId ? `${apiBaseUrl}/BySubCustomer/${selectedSubCustomerId}` : apiBaseUrl;
                const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }});
                const data = await res.json();
                setSurcharges((data || []).filter(s => isVisibleSurcharge(s, includeInactive)));
            } finally {
                setLoading(false);
            }
        })();
    };

    return (
        <Container className="mt-5">
            <Card>
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <CardTitle tag="h5" className="mb-0">Surcharge Management</CardTitle>
                        <Button
                            color="primary"
                            onClick={handleOpenNew}
                        >
                            + New Surcharge
                        </Button>
                    </div>

                    <Row className="mb-3">
                        <Col md={6}>
                            <FormGroup className="position-relative">
                                <SearchableDropdownFilter
                                    value={selectedSubCustomerId}
                                    options={subCustomers}
                                    placeholder="Filter by Sub-Customer..."
                                    getOptionLabel={getSubCustomerLabel}
                                    onChange={setSelectedSubCustomerId}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6} className="d-flex align-items-center">
                            <FormGroup check className="mb-0">
                                <Label check>
                                    <Input
                                        id="includeInactiveSurcharges"
                                        type="checkbox"
                                        checked={includeInactive}
                                        onChange={(e) => setIncludeInactive(e.target.checked)}
                                    />{" "}
                                    Show inactive surcharges
                                </Label>
                            </FormGroup>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="py-5 text-center">
                            <Spinner />
                        </div>
                    ) : (
                        <Table bordered hover responsive>
                            <thead className="table-light">
                                <tr>
                                    <SortableHeader label="Sub-Customer" sortKey="subCustomer" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Route" sortKey="route" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Surcharge" sortKey="description" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Transaction Type" sortKey="transactionType" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Calculation Type" sortKey="surchargeType" sortConfig={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Amount" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} style={{ textAlign: "right" }} />
                                    <SortableHeader label="Status" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSurcharges.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center">No surcharges found</td></tr>
                                ) : sortedSurcharges.map(s => (
                                    <tr key={s.id} className={s.isActive === false ? 'text-muted fst-italic' : ''}>
                                        <td>{s.subCustomer?.name ?? ""}</td>
                                        <td>{s.route?.name ?? (s.targetType === "SubCustomer" || (!s.targetType && !s.driverId) ? <span className="text-muted">Default (all routes)</span> : "")}</td>
                                        <td>{s.description}</td>
                                        <td>{s.transactionType?.transactionName ?? s.transactionTypeId}</td>
                                        <td>{s.surchargeType?.name ?? "Dollar Amount"}</td>
                                        <td style={{ textAlign: 'right' }}>{s.surchargeType?.name === 'Percentage' ? `${Number(s.percentage).toFixed(2)}%` : `$${Number(s.dollarAmount).toFixed(2)}`}</td>
                                        <td>{s.isActive === false ? <span className="text-muted">Inactive</span> : "Active"}</td>
                                        <td className="text-end">
                                            <Button size="sm" color="info" onClick={() => { setEditing(s); setOpen(true); }}>
                                                Edit
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={open} toggle={() => setOpen(false)} size="lg">
                <ModalHeader toggle={() => setOpen(false)}>
                    {editing ? "Edit Surcharge" : "New Surcharge"}
                </ModalHeader>
                <ModalBody>
                    <SurchargeForm
                        surchargeItem={
                            editing ?? {
                                id: 0,
                                subCustomerId: selectedSubCustomerId, // prefill with selected
                                targetType: "SubCustomer",
                            }
                        }
                        apiBaseUrl={apiBaseUrl}
                        allowedTargetTypes={["SubCustomer", "Route"]}
                        defaultTargetType="SubCustomer"
                        onSuccess={handleSaved}
                        onCancel={() => setOpen(false)}
                    />
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default SurchargeGrid;
